import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { getOrganizationsDashboardSummary } from '../organizations.api';
import type { GetOrganizationsAndSubscriptionDataParams } from '../organizations.api';
import type {
    OrganizationsDashboardTopOrganizationsByCommissions,
    SponsorDownlinePastDueRow,
    SponsorLeaderboardRow,
} from '../organizations.types';
import type { OrganizationsDashboardSummaryData } from '../organizations.types';

export interface OrganizationsMetricsBiPanelProps {
    searchParams: GetOrganizationsAndSubscriptionDataParams;
    podDiscrepancyFilter: string;
    onApiError?: (message: string) => void;
}

function buildSummaryRequestParams(
    searchParams: GetOrganizationsAndSubscriptionDataParams,
    podDiscrepancyFilter: string,
): GetOrganizationsAndSubscriptionDataParams {
    const merged: GetOrganizationsAndSubscriptionDataParams = { ...searchParams };
    if (podDiscrepancyFilter !== 'No filter') {
        merged.org_pod_discrepancy = podDiscrepancyFilter;
    } else {
        delete merged.org_pod_discrepancy;
    }
    if (!merged.start_date) {
        delete merged.start_date;
    }
    if (!merged.end_date) {
        delete merged.end_date;
    }
    if (!merged.is_problematic_account) {
        delete merged.is_problematic_account;
    }
    delete merged.sort;
    delete merged.page;
    delete merged.per_page;
    return merged;
}

function formatUsdFromCents(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
}

const TIER_LABELS: Record<string, string> = {
    gold: 'Gold',
    silver: 'Silver',
    bronze: 'Bronze',
    no_tier: 'No tier',
};

/** Merge legacy "no tier" (space) API keys with no_tier so the chart never shows duplicates */
function mergeTierCountsForDisplay(raw: Record<string, number>): Record<string, number> {
    const out: Record<string, number> = { gold: 0, silver: 0, bronze: 0, no_tier: 0 };
    Object.entries(raw).forEach(([k, v]) => {
        const norm = k.trim().toLowerCase().replace(/\s+/g, '_');
        let bucket: keyof typeof out;
        if (norm === 'gold') {
            bucket = 'gold';
        } else if (norm === 'silver') {
            bucket = 'silver';
        } else if (norm === 'bronze') {
            bucket = 'bronze';
        } else {
            bucket = 'no_tier';
        }
        out[bucket] += v;
    });
    return out;
}

function SponsorLeaderboardTable({ rows }: { rows: SponsorLeaderboardRow[] }): React.ReactElement {
    return (
        <TableContainer sx={{ maxHeight: 200 }}>
            <Table size="small" stickyHeader padding="none">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 28, py: 0.5 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Sponsor</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, pr: 0.5, py: 0.5 }}>
                            Orgs
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={3}
                                sx={{ color: 'text.disabled', fontSize: '0.75rem', py: 0.75 }}
                            >
                                —
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((r, i) => (
                            <TableRow key={`${r.sponsor_name}-${i}`}>
                                <TableCell sx={{ fontSize: '0.75rem', py: 0.4 }}>{i + 1}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', py: 0.4 }}>
                                    {r.sponsor_name}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontSize: '0.75rem', pr: 0.5, py: 0.4 }}
                                >
                                    {r.organization_count}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function SponsorLeaderboardCard({
    title,
    rows,
}: {
    title: string;
    rows: SponsorLeaderboardRow[];
}): React.ReactElement {
    return (
        <Card
            variant="outlined"
            sx={{
                flex: '1 1 168px',
                minWidth: 156,
                maxWidth: 240,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardContent
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    py: 1.25,
                    px: 1.25,
                    '&:last-child': { pb: 1.25 },
                }}
            >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75, lineHeight: 1.2 }}>
                    {title}
                </Typography>
                <SponsorLeaderboardTable rows={rows} />
            </CardContent>
        </Card>
    );
}

function SponsorDownlinePastDueAndTopCommissionsSection({
    pastDue,
    topOrganizationsByCommissions,
    topNFallback,
}: {
    pastDue: { top_n: number; rows: SponsorDownlinePastDueRow[] };
    topOrganizationsByCommissions: OrganizationsDashboardTopOrganizationsByCommissions;
    topNFallback: number;
}): React.ReactElement {
    const pastTopN = pastDue.top_n > 0 ? pastDue.top_n : topNFallback;
    const pastRows = pastDue.rows ?? [];
    const commTopN =
        topOrganizationsByCommissions.top_n > 0
            ? topOrganizationsByCommissions.top_n
            : topNFallback;
    const commRows = topOrganizationsByCommissions.rows ?? [];

    return (
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Sponsor downline past due
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Top {pastTopN} sponsors by total <strong>amount due</strong> on{' '}
                    <strong>direct</strong> sponsored orgs.
                </Typography>
                {pastRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No sponsors with downline past due in the current filter.
                    </Typography>
                ) : (
                    <Card variant="outlined" sx={{ maxWidth: 560 }}>
                        <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                            <TableContainer>
                                <Table size="small" padding="none">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, py: 0.5 }}>
                                                #
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, py: 0.5 }}>
                                                Sponsor
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontWeight: 700, py: 0.5 }}
                                            >
                                                Orgs past due
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontWeight: 700, py: 0.5, pr: 0.5 }}
                                            >
                                                Total due
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pastRows.map((r, i) => (
                                            <TableRow key={`${r.sponsor_name}-${i}`}>
                                                <TableCell sx={{ fontSize: '0.75rem', py: 0.45 }}>
                                                    {i + 1}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem', py: 0.45 }}>
                                                    {r.sponsor_name}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{ fontSize: '0.75rem', py: 0.45 }}
                                                >
                                                    {r.organizations_with_past_due}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        py: 0.45,
                                                        pr: 0.5,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {formatUsdFromCents(r.total_amount_past_due)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Top organizations by commissions
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Top {commTopN} organizations by <strong>total commissions earned</strong>.
                    Respects current filters.
                </Typography>
                {commRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No organizations with commissions in the current filter.
                    </Typography>
                ) : (
                    <Card variant="outlined" sx={{ maxWidth: 560 }}>
                        <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                            <TableContainer>
                                <Table size="small" padding="none">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, py: 0.5 }}>
                                                #
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, py: 0.5 }}>
                                                Organization
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ fontWeight: 700, py: 0.5, pr: 0.5 }}
                                            >
                                                Total commissions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {commRows.map((r, i) => (
                                            <TableRow key={`${r.organization_name}-${i}`}>
                                                <TableCell sx={{ fontSize: '0.75rem', py: 0.45 }}>
                                                    {i + 1}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem', py: 0.45 }}>
                                                    {r.organization_name}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        py: 0.45,
                                                        pr: 0.5,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {formatUsdFromCents(r.total_commissions)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}
            </Grid>
        </Grid>
    );
}

const OrganizationsMetricsBiPanel: React.FC<OrganizationsMetricsBiPanelProps> = ({
    searchParams,
    podDiscrepancyFilter,
    onApiError,
}) => {
    const theme = useTheme();
    const [summary, setSummary] = useState<OrganizationsDashboardSummaryData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSummary = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = buildSummaryRequestParams(searchParams, podDiscrepancyFilter);
            const res = await getOrganizationsDashboardSummary(params);
            setSummary(res.data.data);
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Failed to load dashboard summary';
            setError(msg);
            setSummary(null);
            onApiError?.(msg);
        } finally {
            setLoading(false);
        }
    }, [searchParams, podDiscrepancyFilter, onApiError]);

    useEffect(() => {
        const id = window.setTimeout(() => {
            fetchSummary();
        }, 400);
        return () => window.clearTimeout(id);
    }, [fetchSummary]);

    if (loading && !summary) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (error && !summary) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!summary) {
        return null;
    }

    const mergedTierCounts = mergeTierCountsForDisplay(summary.tier_counts);

    const tierChartData = Object.entries(mergedTierCounts).map(([key, value]) => ({
        tier: TIER_LABELS[key] || key,
        organizations: value,
    }));

    const uploadTrendIcon =
        summary.comparisons.uploads_prior_30_vs_last_30_direction === 'up' ? (
            <TrendingUpIcon fontSize="small" color="success" />
        ) : summary.comparisons.uploads_prior_30_vs_last_30_direction === 'down' ? (
            <TrendingDownIcon fontSize="small" color="error" />
        ) : (
            <TrendingFlatIcon fontSize="small" color="disabled" />
        );

    const leaderboards = summary.sponsor_leaderboards ?? {
        top_n: 10,
        with_paid_subscription: [],
        by_tier: { gold: [], silver: [], bronze: [], no_tier: [] },
    };
    const byTier = leaderboards.by_tier;
    const sponsorLeaderboardsAllEmpty =
        leaderboards.with_paid_subscription.length === 0 &&
        byTier.gold.length === 0 &&
        byTier.silver.length === 0 &&
        byTier.bronze.length === 0 &&
        (byTier.no_tier?.length ?? 0) === 0;

    return (
        <Paper
            elevation={1}
            sx={{
                p: 2,
                mb: 3,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Business intelligence overview
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Sponsor leaderboard
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Top {leaderboards.top_n} sponsors by org count per card.{' '}
                    <strong>All Subscriptions</strong> counts gold, silver, and bronze only;{' '}
                    <strong>Cancelled</strong> is orgs with no paid tier. Respects current filters.
                </Typography>
                {sponsorLeaderboardsAllEmpty ? (
                    <Typography variant="body2" color="text.secondary">
                        No sponsored orgs in current filter.
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.25,
                            alignItems: 'stretch',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <SponsorLeaderboardCard
                            title="All Subscriptions"
                            rows={leaderboards.with_paid_subscription}
                        />
                        <SponsorLeaderboardCard title="Gold" rows={byTier.gold} />
                        <SponsorLeaderboardCard title="Silver" rows={byTier.silver} />
                        <SponsorLeaderboardCard title="Bronze" rows={byTier.bronze} />
                        <SponsorLeaderboardCard title="Cancelled" rows={byTier.no_tier ?? []} />
                    </Box>
                )}
            </Box>

            <SponsorDownlinePastDueAndTopCommissionsSection
                pastDue={
                    summary.sponsor_downline_past_due ?? {
                        top_n: leaderboards.top_n,
                        rows: [],
                    }
                }
                topOrganizationsByCommissions={
                    summary.top_organizations_by_commissions ?? {
                        top_n: leaderboards.top_n,
                        rows: [],
                    }
                }
                topNFallback={leaderboards.top_n}
            />

            {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent
                            sx={{
                                py: 1.25,
                                px: 1.5,
                                '&:last-child': { pb: 1.25 },
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Organizations in view
                            </Typography>
                            <Typography variant="h5">{summary.organization_count}</Typography>
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {Object.entries(mergedTierCounts).map(([k, v]) => (
                                    <Chip
                                        key={k}
                                        size="small"
                                        label={`${TIER_LABELS[k] || k}: ${v}`}
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent
                            sx={{
                                py: 1.25,
                                px: 1.5,
                                '&:last-child': { pb: 1.25 },
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Product adoption (uploads, all orgs)
                            </Typography>
                            <Typography variant="body2">
                                Last 30d:{' '}
                                <strong>
                                    {summary.totals.uploads_last_30_days.toLocaleString()}
                                </strong>
                            </Typography>
                            <Typography variant="body2">
                                Last 60d:{' '}
                                <strong>
                                    {summary.totals.uploads_last_60_days.toLocaleString()}
                                </strong>
                            </Typography>
                            <Typography variant="body2">
                                Last 90d:{' '}
                                <strong>
                                    {summary.totals.uploads_last_90_days.toLocaleString()}
                                </strong>
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                {uploadTrendIcon}
                                <Typography variant="caption" color="text.secondary">
                                    Prior 30d window (agg.):{' '}
                                    {summary.comparisons.uploads_prior_30_days_window_sum.toLocaleString()}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent
                            sx={{
                                py: 1.25,
                                px: 1.5,
                                '&:last-child': { pb: 1.25 },
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Pods &amp; commissions (totals)
                            </Typography>
                            <Typography variant="body2">
                                Org pods / org pod subs:{' '}
                                <strong>
                                    {summary.totals.pods_org.toLocaleString()} /{' '}
                                    {summary.totals.org_pod_subscriptions.toLocaleString()}
                                </strong>
                            </Typography>
                            <Typography variant="body2">
                                Athlete pods / ath. pod subs:{' '}
                                <strong>
                                    {summary.totals.pods_athlete.toLocaleString()} /{' '}
                                    {summary.totals.athlete_pod_subscriptions.toLocaleString()}
                                </strong>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Total commissions:{' '}
                                <strong>
                                    {formatUsdFromCents(summary.totals.total_commissions)}
                                </strong>
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                        Current tier distribution
                    </Typography>
                    <Box sx={{ width: '100%', height: 240 }}>
                        <ResponsiveContainer>
                            <BarChart
                                data={tierChartData}
                                margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="tier" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Bar
                                    dataKey="organizations"
                                    fill={theme.palette.primary.main}
                                    name="Orgs"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                        Uploads by month (filtered orgs, ~last 3 months)
                    </Typography>
                    <Box sx={{ width: '100%', height: 240 }}>
                        <ResponsiveContainer>
                            <LineChart
                                data={summary.series.uploads_by_month}
                                margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="total_uploads"
                                    name="Uploads"
                                    stroke={theme.palette.secondary.main}
                                    strokeWidth={2}
                                    dot
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                </Grid>
            </Grid>

            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 2 }}>
                {summary.notes?.tier_counts_by_month}
            </Typography>
        </Paper>
    );
};

export default OrganizationsMetricsBiPanel;
